import { useState } from "react";
import { X, Plus, Trash2, Check } from "lucide-react";
import NameSuggest, { rememberNames } from "@/components/NameSuggest";
import { PERFORMER_TYPES, PERFORMER_ROLES, STUDIO_ROLES, LABEL_ROLES, COMPOSITION_ROLES } from "@/lib/releaseTaxonomy";

const TABS = [
  { key: "performers", label: "Intérpretes", desc: "Para grupos, cada miembro debe incluirse como un intérprete separado." },
  { key: "studio", label: "Personal de Estudio", desc: "Colaboradores como Productores, Ingenieros de Mezcla y Masterización." },
  { key: "label", label: "Personal de Discográfica", desc: "Colaboradores como Administradores y Coordinadores A&R." },
  { key: "composition", label: "Composicionales", desc: "Colaboradores como Arreglistas y Letristas." },
];

const empty = { performers: [], studio: [], label: [], composition: [] };
const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-black dark:border-white/15 dark:bg-white/5 dark:text-white";

export function ContributorsModal({ value, onSave, onClose, trackTitle, suggestions = [] }) {
  const [tab, setTab] = useState("performers");
  const [data, setData] = useState(() => ({ ...empty, ...(value || {}) }));

  const rows = data[tab] || [];
  const setRows = (next) => setData((d) => ({ ...d, [tab]: next }));
  const addRow = () => setRows([...rows, tab === "performers" ? { type: PERFORMER_TYPES[0], name: "", role: PERFORMER_ROLES[0] } : { name: "", role: "" }]);
  const upd = (i, k, v) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const del = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const roleOpts = tab === "performers" ? PERFORMER_ROLES : tab === "studio" ? STUDIO_ROLES : tab === "label" ? LABEL_ROLES : COMPOSITION_ROLES;
  const addLabels = { performers: "intérprete", studio: "personal de estudio", label: "personal de discográfica", composition: "colaboradores composicionales" };
  const current = TABS.find((t) => t.key === tab);

  const save = () => {
    const clean = {};
    for (const k of Object.keys(empty)) clean[k] = (data[k] || []).filter((r) => r.name && r.name.trim());
    rememberNames(Object.values(clean).flat().map((r) => r.name));
    onSave(clean);
  };

  const total = Object.values(data).reduce((a, arr) => a + (arr || []).filter((r) => r.name?.trim()).length, 0);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="contributors-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-white/10 dark:bg-[#161616]">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 dark:border-white/10">
          <div>
            <h3 className="font-serif text-lg font-bold text-black dark:text-white">Contribuidores</h3>
            {trackTitle ? <p className="text-xs text-slate-500">{trackTitle}</p> : null}
          </div>
          <button data-testid="contributors-close" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex flex-wrap gap-x-5 gap-y-1 border-b border-slate-100 px-6 dark:border-white/10">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`contrib-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`-mb-px border-b-2 py-3 text-xs font-semibold uppercase tracking-wide transition-colors ${tab === t.key ? "border-[#AC3333] text-black dark:text-white" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <p className="mb-4 text-sm text-slate-500">{current?.desc}</p>
          {rows.length === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-300 py-6 text-center text-sm text-slate-400 dark:border-white/15">Aún no hay colaboradores en esta sección.</p>
          ) : (
            <div className="space-y-3">
              {rows.map((r, i) => (
                <div key={i} className="flex items-start gap-2" data-testid={`contrib-row-${tab}-${i}`}>
                  <span className="mt-2.5 w-4 text-xs text-slate-400">{i + 1}</span>
                  {tab === "performers" && (
                    <select data-testid={`contrib-type-${tab}-${i}`} value={r.type} onChange={(e) => upd(i, "type", e.target.value)} className={`${inputCls} max-w-[180px]`}>
                      {PERFORMER_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  )}
                  <NameSuggest testId={`contrib-name-${tab}-${i}`} value={r.name} onChange={(v) => upd(i, "name", v)} suggestions={suggestions} placeholder="Nombre legal (escribe y elige)" className={inputCls} />
                  <select data-testid={`contrib-role-${tab}-${i}`} value={r.role} onChange={(e) => upd(i, "role", e.target.value)} className={`${inputCls} max-w-[220px]`}>
                    <option value="">Seleccionar Rol</option>
                    {roleOpts.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <button data-testid={`contrib-del-${tab}-${i}`} onClick={() => del(i)} className="mt-0.5 rounded-md border border-red-200 bg-red-50 p-2 text-red-600 hover:border-red-400 dark:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
          <button data-testid={`contrib-add-${tab}`} onClick={addRow} className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-slate-50 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-600 hover:border-black dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
            <Plus className="h-3.5 w-3.5" /> Añadir {addLabels[tab]}
          </button>
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50/60 px-6 py-4 dark:border-white/10 dark:bg-white/5">
          <span className="text-xs text-slate-500">{total} colaborador{total === 1 ? "" : "es"} en total</span>
          <div className="flex gap-3">
            <button data-testid="contributors-cancel" onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium hover:border-black dark:border-white/15 dark:text-slate-200">Cancelar</button>
            <button data-testid="contributors-done" onClick={save} className="flex items-center gap-2 rounded-lg bg-[#AC3333] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#FF0505]"><Check className="h-4 w-4" /> Hecho</button>
          </div>
        </div>
      </div>
    </div>
  );
}
